import { useQuery } from "@apollo/client";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { IconButton, Menu, MenuItem, TextField, Tooltip } from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { Button, Modal, Tag } from "pepsico-ds";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  listSegments,
  listSegmentsPaginated,
} from "../../../../graphql/queries/listSegments";
import DeleteConfirmationModal from "../../../common/DeleteConfirmationModal/DeleteConfirmationModal";
import Table from "../../../common/table/Table";
import SegmentSelect from "../../segmentSelect/SegmentSelect";
import BoosterStatusTag from "./BoosterStatusTag";
import {
  createEmptyBooster,
  filterBoosters,
  getBoosterValidationErrors,
} from "./pointsOutcomeUtils";
import TableFilterBar from "./TableFilterBar";
import { TIME_BOUND_FILTER_FIELDS } from "./tableFilterConfig";

const SEGMENTS_PER_PAGE = 12;
const EMPTY_FILTERS = {
  name: "",
  type: "",
  startDate: "",
  endDate: "",
  status: "",
};
const FILTER_FIELDS = [
  {
    key: "name",
    label: "Booster name",
    placeholder: "Filter by name",
  },
  {
    key: "type",
    label: "Type",
    type: "select",
    emptyOptionLabel: "All types",
    options: [
      { value: "Multiplier", label: "Multiplier" },
      { value: "Flat points", label: "Flat points" },
    ],
  },
  ...TIME_BOUND_FILTER_FIELDS,
];

const BoosterTable = ({
  title = "Time-Bound Booster",
  description = "Configure campaign-specific boosters that temporarily increase points or add bonus flat points.",
  boosterMultipliers = [],
  boosterFlatPoints = [],
  onChangeMultipliers,
  onChangeFlatPoints,
  onValidationChange,
}) => {
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [touchedRows, setTouchedRows] = useState({});

  // Segment modal state
  const [segmentModalOpen, setSegmentModalOpen] = useState(false);
  const [segmentModalField, setSegmentModalField] = useState(null); // { boosterId, boosterType, field }
  const [segmentSearchKeyword, setSegmentSearchKeyword] = useState(undefined);
  const [segmentPage, setSegmentPage] = useState(1);
  const [selectedSegments, setSelectedSegments] = useState([]);

  // Segments queries
  const { data: segmentsData } = useQuery(listSegmentsPaginated, {
    variables: {
      filter: { status: "ENABLED", keyword: segmentSearchKeyword },
      limit: SEGMENTS_PER_PAGE,
      offset: (segmentPage - 1) * SEGMENTS_PER_PAGE,
    },
  });

  const { data: allSegmentsData } = useQuery(listSegments, {
    variables: { filter: { status: "ENABLED" } },
  });

  const segments = useMemo(
    () => segmentsData?.listSegmentsPaginated?.items || [],
    [segmentsData]
  );

  const segmentsTotalPages = useMemo(
    () =>
      Math.ceil(
        (segmentsData?.listSegmentsPaginated?.totalCount || 0) / SEGMENTS_PER_PAGE
      ) || 1,
    [segmentsData]
  );

  const allSegmentsMap = useMemo(() => {
    const map = {};
    (allSegmentsData?.listSegments || []).forEach((s) => {
      map[s.id] = s.name;
    });
    return map;
  }, [allSegmentsData]);

  const allBoosters = useMemo(() => {
    const multipliers = (boosterMultipliers || []).map((b) => ({
      ...b,
      _boosterType: "Multiplier",
    }));
    const flatPts = (boosterFlatPoints || []).map((b) => ({
      ...b,
      _boosterType: "Flat points",
    }));
    return [...multipliers, ...flatPts];
  }, [boosterMultipliers, boosterFlatPoints]);

  // Compute validation errors only for touched rows
  const errors = useMemo(() => {
    return allBoosters.reduce((acc, booster) => {
      // Only validate rows that have been touched
      if (!touchedRows[booster.id]) {
        return acc;
      }
      const boosterErrors = getBoosterValidationErrors(booster, allBoosters);
      if (Object.keys(boosterErrors).length > 0) {
        acc[booster.id] = boosterErrors;
      }
      return acc;
    }, {});
  }, [allBoosters, touchedRows]);

  const hasValidationErrors = Object.keys(errors).length > 0;

  useEffect(() => {
    onValidationChange?.(hasValidationErrors);
  }, [hasValidationErrors, onValidationChange]);

  const filteredBoosters = useMemo(
    () => filterBoosters(allBoosters, filters),
    [allBoosters, filters]
  );

  const updateFilter = (field, value) => {
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
  };

  const hasActiveFilters = Object.values(filters).some(Boolean);

  const handleAddClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleAddBooster = useCallback(
    (type) => {
      setMenuAnchorEl(null);
      const newEntry = createEmptyBooster(type);
      if (type === "Multiplier") {
        onChangeMultipliers([...boosterMultipliers, newEntry]);
      } else {
        onChangeFlatPoints([...boosterFlatPoints, newEntry]);
      }
    },
    [boosterMultipliers, boosterFlatPoints, onChangeMultipliers, onChangeFlatPoints]
  );

  const updateBooster = useCallback(
    (id, boosterType, field, value) => {
      // Mark the row as touched when user modifies any field
      setTouchedRows((prev) => ({ ...prev, [id]: true }));
      if (boosterType === "Multiplier") {
        const updated = boosterMultipliers.map((b) =>
          b.id === id ? { ...b, [field]: value } : b
        );
        onChangeMultipliers(updated);
      } else {
        const updated = boosterFlatPoints.map((b) =>
          b.id === id ? { ...b, [field]: value } : b
        );
        onChangeFlatPoints(updated);
      }
    },
    [boosterMultipliers, boosterFlatPoints, onChangeMultipliers, onChangeFlatPoints]
  );

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteTarget) return;
    const { id, _boosterType } = deleteTarget;
    if (_boosterType === "Multiplier") {
      onChangeMultipliers(boosterMultipliers.filter((b) => b.id !== id));
    } else {
      onChangeFlatPoints(boosterFlatPoints.filter((b) => b.id !== id));
    }
    setDeleteTarget(null);
  }, [
    deleteTarget,
    boosterMultipliers,
    boosterFlatPoints,
    onChangeMultipliers,
    onChangeFlatPoints,
  ]);

  // Segment modal handlers
  const openSegmentModal = useCallback(
    (boosterId, boosterType, field) => {
      const booster = allBoosters.find((b) => b.id === boosterId);
      const ids = booster?.[field] || [];
      const preSelected = ids
        .map((id) => (allSegmentsMap[id] ? { id, name: allSegmentsMap[id] } : null))
        .filter(Boolean);
      setSelectedSegments(preSelected);
      setSegmentModalField({ boosterId, boosterType, field });
      setSegmentPage(1);
      setSegmentSearchKeyword(undefined);
      setSegmentModalOpen(true);
    },
    [allBoosters, allSegmentsMap]
  );

  const handleSegmentSave = useCallback(() => {
    if (!segmentModalField) return;
    const { boosterId, boosterType, field } = segmentModalField;
    const ids = selectedSegments.map((s) => s.id);
    updateBooster(boosterId, boosterType, field, ids);
    setSegmentModalOpen(false);
    setSegmentModalField(null);
  }, [segmentModalField, selectedSegments, updateBooster]);

  const handleSegmentSearch = useCallback((value) => {
    setSegmentSearchKeyword(value || undefined);
    setSegmentPage(1);
  }, []);

  const getSegmentNames = (ids) => {
    if (!ids?.length) return [];
    return ids.map((id) => allSegmentsMap[id]).filter(Boolean);
  };

  const headerData = useMemo(
    () => [
      {
        columnKey: "name",
        header: "BOOSTER NAME *",
        render: (row) => (
          <TextField
            size="small"
            required
            placeholder="Enter name"
            value={row._rawName ?? ""}
            error={!!errors[row.id]?.name}
            onChange={(e) =>
              updateBooster(row.id, row._boosterType, "name", e.target.value)
            }
            style={{ width: 140 }}
          />
        ),
      },
      {
        columnKey: "type",
        header: "TYPE",
        render: (row) => (
          <span
            style={{
              display: "inline-block",
              padding: "2px 8px",
              borderRadius: 4,
              fontSize: "0.75rem",
              fontWeight: 500,
              background: row._boosterType === "Multiplier" ? "#e3f2fd" : "#fff3e0",
              color: row._boosterType === "Multiplier" ? "#1565c0" : "#e65100",
            }}
          >
            {row._boosterType}
          </span>
        ),
      },
      {
        columnKey: "value",
        header: "VALUE *",
        render: (row) => (
          <TextField
            size="small"
            type="number"
            required
            placeholder={row._boosterType === "Multiplier" ? "e.g. 2.0" : "e.g. 100"}
            value={row._rawValue ?? ""}
            error={!!errors[row.id]?.value}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "" || Number(val) >= 0) {
                updateBooster(row.id, row._boosterType, "value", val);
              }
            }}
            inputProps={{ min: 0 }}
            InputProps={{
              endAdornment: (
                <span style={{ fontSize: "0.8rem", color: "#666" }}>
                  {row._boosterType === "Multiplier" ? "x" : "pts"}
                </span>
              ),
            }}
            style={{ width: 100 }}
          />
        ),
      },
      {
        columnKey: "include_segments",
        header: "INCLUDE SEGMENTS",
        render: (row) => {
          const names = getSegmentNames(row._rawIncludeSegments);
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Button
                size="small"
                variant="secondaryOutline"
                onClick={() =>
                  openSegmentModal(row.id, row._boosterType, "include_segments")
                }
              >
                {names.length ? `${names.length} selected` : "Select"}
              </Button>
              {names.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  {names.slice(0, 2).map((name) => (
                    <Tag
                      key={name}
                      text={name}
                      color="Green-150"
                      type="filled"
                      size="small"
                      isCopyable={false}
                      className="segment-tag"
                    />
                  ))}
                  {names.length > 2 && (
                    <Tag
                      text={`+${names.length - 2}`}
                      color="Gray-100"
                      type="filled"
                      size="small"
                      isCopyable={false}
                      className="segment-tag"
                    />
                  )}
                </div>
              )}
            </div>
          );
        },
      },
      {
        columnKey: "exclude_segments",
        header: "EXCLUDE SEGMENTS",
        render: (row) => {
          const names = getSegmentNames(row._rawExcludeSegments);
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Button
                size="small"
                variant="secondaryOutline"
                onClick={() =>
                  openSegmentModal(row.id, row._boosterType, "exclude_segments")
                }
              >
                {names.length ? `${names.length} selected` : "Select"}
              </Button>
              {names.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  {names.slice(0, 2).map((name) => (
                    <Tag
                      key={name}
                      text={name}
                      color="Red-150"
                      type="filled"
                      size="small"
                      isCopyable={false}
                      className="segment-tag"
                    />
                  ))}
                  {names.length > 2 && (
                    <Tag
                      text={`+${names.length - 2}`}
                      color="Gray-100"
                      type="filled"
                      size="small"
                      isCopyable={false}
                      className="segment-tag"
                    />
                  )}
                </div>
              )}
            </div>
          );
        },
      },
      {
        columnKey: "start_date",
        header: "START DATE *",
        render: (row) => {
          const now = moment();
          return (
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DateTimePicker
                value={
                  row.effective_start_date ? moment(row.effective_start_date) : null
                }
                minDateTime={now}
                onAccept={(val) => {
                  updateBooster(
                    row.id,
                    row._boosterType,
                    "effective_start_date",
                    val ? val.toISOString() : ""
                  );
                }}
                timeSteps={{ minutes: 1 }}
                closeOnSelect={false}
                slotProps={{
                  textField: {
                    size: "small",
                    required: true,
                    style: { width: 160 },
                    error:
                      !!errors[row.id]?.startDate || !!errors[row.id]?.dateRange,
                  },
                  actionBar: {
                    actions: ["clear", "accept"],
                  },
                }}
              />
            </LocalizationProvider>
          );
        },
      },
      {
        columnKey: "end_date",
        header: "END DATE *",
        render: (row) => {
          const now = moment();
          const minDateTime = row.effective_start_date
            ? moment(row.effective_start_date)
            : now;
          return (
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DateTimePicker
                value={
                  row.effective_end_date ? moment(row.effective_end_date) : null
                }
                minDateTime={minDateTime.isAfter(now) ? minDateTime : now}
                onAccept={(val) => {
                  updateBooster(
                    row.id,
                    row._boosterType,
                    "effective_end_date",
                    val ? val.toISOString() : ""
                  );
                }}
                timeSteps={{ minutes: 1 }}
                closeOnSelect={false}
                slotProps={{
                  textField: {
                    size: "small",
                    required: true,
                    style: { width: 160 },
                    error: !!errors[row.id]?.endDate || !!errors[row.id]?.dateRange,
                  },
                  actionBar: {
                    actions: ["clear", "accept"],
                  },
                }}
              />
            </LocalizationProvider>
          );
        },
      },
      {
        columnKey: "status",
        header: "STATUS",
        render: (row) => (
          <BoosterStatusTag
            startDate={row.effective_start_date}
            endDate={row.effective_end_date}
          />
        ),
      },
      {
        columnKey: "actions",
        header: "",
        render: (row) => (
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => setDeleteTarget(row)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateBooster, errors, allSegmentsMap, openSegmentModal]
  );

  const rowData = useMemo(
    () =>
      filteredBoosters.map((b) => ({
        ...b,
        _rawName: b.name || "",
        _rawValue: b.value,
        _rawIncludeSegments: b.include_segments || [],
        _rawExcludeSegments: b.exclude_segments || [],
      })),
    [filteredBoosters]
  );

  return (
    <div className="section-card booster-section">
      <div className="section-header">
        <div>
          <h3>{title}</h3>
          {description && <p className="section-description">{description}</p>}
        </div>
        <div>
          <Button
            size="small"
            variant="primary"
            onClick={handleAddClick}
            iconTrailing="arrow_drop_down"
          >
            Add booster
          </Button>
          <Menu
            anchorEl={menuAnchorEl}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem
              onClick={() => handleAddBooster("Multiplier")}
              sx={{ minWidth: "180px" }}
            >
              Multiplier
            </MenuItem>
            <MenuItem
              onClick={() => handleAddBooster("Flat points")}
              sx={{ minWidth: "180px" }}
            >
              Flat points
            </MenuItem>
          </Menu>
        </div>
      </div>

      <TableFilterBar
        filters={filters}
        fields={FILTER_FIELDS}
        onChange={updateFilter}
        onClear={clearFilters}
      />

      <Table
        headerData={headerData}
        rowData={rowData}
        pagination
        rowsPerPage={10}
        noDataLabel={
          hasActiveFilters
            ? "No boosters match the filters"
            : "No boosters configured"
        }
      />

      {hasValidationErrors && (
        <div className="validation-error">
          {[
            ...new Set(
              Object.values(errors).flatMap((entryErrors) =>
                Object.values(entryErrors)
              )
            ),
          ].map((msg, idx) => (
            <div key={idx}>
              <span>&#9888;</span> {msg}
            </div>
          ))}
        </div>
      )}

      {/* Segment selection modal */}
      <Modal
        showModal={segmentModalOpen}
        title={
          segmentModalField?.field === "include_segments"
            ? "Choose Include Segments"
            : "Choose Exclude Segments"
        }
        text=""
        className="campaign-segment-select-modal"
        onCloseModal={() => {
          setSegmentModalOpen(false);
          setSegmentModalField(null);
        }}
        primaryButtonProps={{
          text: "Save",
          disabled: false,
          onClick: handleSegmentSave,
        }}
        showSecondaryButton={false}
        showTertiaryButton={false}
        showLink={false}
      >
        <SegmentSelect
          segmentsData={segments}
          selected={selectedSegments}
          onChange={setSelectedSegments}
          selection="multiple"
          currentPage={segmentPage}
          totalPages={segmentsTotalPages}
          onPageChange={setSegmentPage}
          onSearch={handleSegmentSearch}
        />
      </Modal>

      <DeleteConfirmationModal
        isOpen={Boolean(deleteTarget)}
        title="Delete Time-Bounded Rule?"
        message="This action cannot be undone. If the rule is currently active, it will stop being applied immediately."
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default BoosterTable;
